import { type ReactNode, type ElementType, type CSSProperties } from "react";
import { cn } from "@/shared/utils/cn";

type Padding = "none" | "md" | "lg";

const paddingClass: Record<Padding, string> = {
  none: "",
  md: "p-4",
  lg: "p-6",
};

export interface CardProps {
  children: ReactNode;
  padding?: Padding;
  interactive?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
  as?: ElementType;
  className?: string;
  bodyClassName?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

export function Card({
  children,
  padding = "lg",
  interactive = false,
  header,
  footer,
  as: Tag = "section",
  className,
  bodyClassName,
  style,
  onClick,
}: CardProps) {
  return (
    <Tag
      className={cn(
        "rounded-lg border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow-card)]",
        interactive &&
          "cursor-pointer transition hover:border-[var(--line-strong)] hover:shadow-[var(--shadow-md)]",
        className,
      )}
      style={style}
      onClick={onClick}
    >
      {header && (
        <div className="flex items-center justify-between gap-3 border-b border-[var(--line)] px-6 py-4">
          {header}
        </div>
      )}
      <div className={cn(header || footer ? paddingClass[padding] : paddingClass[padding], bodyClassName)}>
        {children}
      </div>
      {footer && (
        <div className="border-t border-[var(--line)] bg-[var(--bg-sunken)] px-6 py-3">
          {footer}
        </div>
      )}
    </Tag>
  );
}

export function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <>
      <div>
        <div className="text-[15px] font-semibold text-[var(--ink-1)]">{title}</div>
        {subtitle && <div className="mt-0.5 text-[12px] text-[var(--ink-3)]">{subtitle}</div>}
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </>
  );
}
